

















































































import { Vue, Component, Prop, Emit } from "vue-property-decorator";
import { namespace } from "vuex-class";

import videoReportingApi from "@/services/api.videoReporting";

enum DragDropUploadState {
	Uploading = "uploading",
	ProcessingUpload = "processing",
}

@Component
export default class DragDropUpload extends Vue {
	$refs: {
		fileInput: HTMLInputElement;
	};

	/**
	 * Accepted file extensions (e.g, ["rtf", "txt"])
	 */
	@Prop({ type: Array, default: () => [] }) private acceptedExtensions: string[];

	/**
	 * Accepted mime types (e.g. ["text/csv", "text/x-csv"])
	 */
	@Prop({ type: Array, default: () => [] }) private acceptedMimeTypes: string[];

	/**
	 * Accepted maximum file size in bytes. If the user tries to upload a file
	 * larger than this, it will fail.
	 */
	@Prop({ type: Number, default: 5 * 1024 * 1024 }) private maxFileSize: number;

	@Prop({ type: Boolean, default: false }) openFileInputOnMount: boolean;

	/**
	 * The method to call to upload the users file. Must accept the file, and a
	 * progress update callback which can be given to axios to track upload
	 * progress.
	 */
	@Prop({ type: Function }) private uploadHandler: (file: File, onProgressUpdate: (number) => void) => Promise<any>;

	private isDraggingFile: boolean = false;
	private draggingDisabled: boolean = false;

	private uploadState: null | DragDropUploadState = null;
	private uploadProgress: number = 0;

	private fileName: string = "";

	@Emit("uploaded")
	public onFileUploaded(file: File, result: any) {}

	@Emit("invalid-file")
	public onInvalidFileDropped(file: File) {}

	private mounted() {
		if (this.openFileInputOnMount) {
			this.$refs.fileInput.click();
		}
	}

	/**
	 * Returns true if the file object represents a file with a correct mime type,
	 * has the correct file extension, and does not exceed the max size.
	 */
	private isFileAllowed(file: File) {
		if (this.acceptedMimeTypes.length == 0 || this.acceptedMimeTypes.includes(file.type)) {
			let fileExt = file.name.split(".").pop();
			if (this.acceptedExtensions.includes(fileExt)) {
				if (file.size <= this.maxFileSize) {
					return true;
				}
			}
		}

		return false;
	}

	/**
	 * Resets our upload state, and invokes our upload handler, passing it the file
	 * and a progress upload handler.
	 */
	private async uploadFile(file: File) {
		// Check the file the user has dropped is valid. If it isn't, we raise an invalid file event.
		if (!this.isFileAllowed(file)) {
			this.onInvalidFileDropped(file);
			return;
		}

		this.uploadState = DragDropUploadState.Uploading;
		this.uploadProgress = 0;
		this.fileName = file.name;

		this.draggingDisabled = false;
		this.isDraggingFile = false;

		let uploadResults = await this.uploadHandler(file, (progress) => {
			this.uploadProgress = progress;

			// If we've uploaded the file, set our state to 'processing', as we're waiting
			// from a response from the server.
			if (this.uploadProgress >= 100) {
				this.uploadState = DragDropUploadState.ProcessingUpload;
			}
		});

		// Wait 1s so we complete any transitions
		setTimeout(() => {
			// If uploadResults is undefined, we assume an error happened.
			// We do this because our axios interceptors prevent us from being
			// able to try/catch the upload call.
			if (uploadResults != undefined) {
				// Raise our uploaded event
				this.onFileUploaded(file, uploadResults);
			} else {
				this.uploadState = null;
				this.uploadProgress = 0;
			}
		}, 1000);
	}

	/**
	 * Handles the drag over event, when the user has dragged something onto our component.
	 */
	private onDragOver(event: DragEvent) {
		if (event.dataTransfer.files) {
			this.isDraggingFile = true;
		}
	}

	/**
	 * Handles the drag leave event, when the user leaves our component without 'dropping'.
	 */
	private onDragLeave() {
		this.isDraggingFile = false;
	}

	/**
	 * Handles the drop event, ensures the user supplied us with a (possibly) valid file.
	 */
	private async onFileDropped(event: DragEvent) {
		let files = event.dataTransfer.files;

		if (files && files.length > 0) {
			let fileToUpload = files[0];

			await this.uploadFile(fileToUpload);
		}

		this.isDraggingFile = false;
	}
}
